"use client";
import React, { ReactNode, useState } from "react";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import {
  formatViews,
  getYoutubeThumbnail,
  getRelativeTime,
  formatDate,
  getBlogsBadge,
  formatTime,
} from "@/utils/commonFunctions";
import RelatedSmallVideoCards from "../cards/RelatedSmallVideoCards";
import { BLOG_ROOT, ROOT_URL } from "@/utils/gloableVariables";
import { Post, RelatableVideoType } from "@/type/commonTypes";
import { useDispatch } from "react-redux";
import { openYTVideoModal, setVideoDataYTVideoModal } from "@/redux/features/ytVideoModal/ytVideoModalSlice";

export default function BlogsAndVideosSection({
  blogs,
  videos,
  children,
}: {
  blogs: Post[];
  videos: RelatableVideoType[];
  children?: ReactNode;
}) {
  const [activeTab, setActiveTab] = useState<"blogs" | "videos">("blogs");

  const dispatch = useDispatch();

  const showBlogs = blogs && blogs.length > 0;
  const showVideos = videos && videos.length > 0;

  if (!showBlogs && !showVideos) return null;

  const featuredBlog = blogs[0];
  const featuredVideo = videos[0];

  const getVideoImageSrc = (video: RelatableVideoType) => {
    const thumbnail = video.video_url ? getYoutubeThumbnail(video.video_url) : null;

    return thumbnail || "/images/default-thumbnail.jpg";
  };

  const getBlogImageSrc = (blog: Post) => {
    return `https://admin.mysiponline.com/app/webroot/blog_uploadimages/${blog.img}`;
  };

  const { label, bgColor } = getBlogsBadge(featuredBlog.bolg_cat || 32);

  return (
    <>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 mb-8">
        <div>{children}</div>

        {showBlogs && showVideos && (
          <div className="w-full xxs:max-w-65.5 sm:max-w-90 flex items-center gap-0.5 bg-white border border-[#D4D4D4] p-0.5 rounded-full">
            {["blogs", "videos"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as "blogs" | "videos")}
                className={`w-1/2 py-2.5 sm:py-3 text-center text-sm sm:text-base leading-1.2! transition capitalize rounded-full ${activeTab === tab ? "bg-(--secondary) font-bold text-white" : "text-primaryBlack hover:text-(--primary) hover:bg-[rgb(var(--primary-alpha)/0.1)]"}`}
              >
                {tab}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-6 sm:gap-8 mb-8">
        {activeTab === "blogs" ? (
          <a
            href={BLOG_ROOT + featuredBlog.permalinks}
            className="group block w-full max-w-206"
          >
            <div className="h-80 xl:h-98.5 flex flex-col lg:flex-row items-stretch bg-white rounded-3xl sm:rounded-8xl overflow-hidden transition border border-[#E4E4E4] shrink-0">
              <div className="w-full h-1/2 lg:h-auto relative overflow-hidden">
                <Image
                  src={getBlogImageSrc(featuredBlog)}
                  alt={featuredBlog.img_alt || featuredBlog.txttitle}
                  fill
                  className="object-cover object-left transition-transform duration-400 group-hover:scale-[1.04]"
                />
              </div>
              <div className="w-full lg:max-w-68 xl:max-w-80 p-4 lg:p-5 xl:p-8 flex flex-col bg-white">
                <span
                  className={[
                    "w-fit text-xs text-bold text-white px-2 py-1 rounded-full bg-[#1976D2] mb-3",
                    bgColor,
                  ].join(" ")}
                >
                  {label}
                </span>

                <p className="text-xl md:text-2xl lg:text-2.5xl xl:text-4xl font-bold text-primaryBlack mb-4 text-left line-clamp-5 transition group-hover:text-primaryBlue">
                  {featuredBlog.txttitle}
                </p>

                <ul className="flex items-center justify-start text-xs font-medium text-[#6E6E73] gap-2 mt-auto">
                  <li className="shrink-0 after:content-['•'] after:inline-block after:ml-2 after:text-[#5c5c5e]">
                    <time dateTime={new Date(featuredBlog.publish_date).toISOString()}>
                      {formatDate(featuredBlog.publish_date ?? new Date(), "dd MMM yyyy")}
                    </time>
                  </li>
                  <li className="shrink-0">{featuredBlog.reading_time}</li>
                  <li className="flex items-center gap-1 ml-auto shrink-0">
                    <Icon.viewEyeIcon />
                    {formatViews(featuredBlog.view)}
                  </li>
                </ul>
              </div>
            </div>
          </a>
        ) : (
          <button
            onClick={() => {
              dispatch(openYTVideoModal());
              dispatch(setVideoDataYTVideoModal(featuredVideo));
            }}
            className="group block w-full max-w-206"
          >
            <div className="h-80 xl:h-98.5 flex flex-col lg:flex-row items-stretch bg-white rounded-3xl sm:rounded-8xl overflow-hidden transition border border-[#E4E4E4] shrink-0">
              <div className="w-full h-1/2 lg:h-auto relative overflow-hidden">
                <div className="absolute inset-0 flex items-center justify-center z-1 bg-black/50 backdrop-blur-xs">
                  <Icon.ytBtnIcon className="w-15 h-auto" />
                </div>

                <Image
                  src={getVideoImageSrc(featuredVideo)}
                  alt={featuredVideo.title ?? "Video Thumbnail"}
                  fill
                  className="object-cover object-left transition-transform duration-400 group-hover:scale-[1.04]"
                />
              </div>
              <div className="w-full lg:max-w-68 xl:max-w-80 p-4 lg:p-5 xl:p-8 flex flex-col bg-white">
                {/* <span
                  className={[
                    "w-fit text-xs text-bold text-white px-2 py-1 rounded-full bg-[#1976D2] mb-3",
                    bgColor,
                  ].join(" ")}
                >
                  {label}
                </span> */}

                <p className="text-xl md:text-2xl lg:text-2.5xl xl:text-4xl font-bold text-primaryBlack mb-4 text-left line-clamp-5 transition group-hover:text-primaryBlue">
                  {featuredVideo.title ?? ""}
                </p>

                <ul className="flex items-center justify-start text-xs font-medium text-[#6E6E73] gap-2 mt-auto">
                  <li className="shrink-0 after:content-['•'] after:inline-block after:ml-2 after:text-[#5c5c5e]">
                    <time dateTime={new Date(featuredVideo.created_at ?? new Date()).toISOString()}>
                      {formatDate(featuredVideo.created_at ?? new Date(), "dd MMM yyyy")}
                    </time>
                  </li>
                  <li className="shrink-0">{formatTime(featuredVideo.duration ?? "")}</li>
                </ul>
              </div>
            </div>
          </button>
        )}

        <div className="sm:bg-white sm:border border-[#E0E0E0] rounded-4xl md:rounded-8xl sm:p-5 xl:p-6 w-full mx-auto max-w-2xl md:mx-0 md:max-w-sm">
          {activeTab === "blogs"
            ? blogs.slice(1, 4).map((blog, ind) => (
                <a
                  key={blog.id ?? ind}
                  href={BLOG_ROOT + blog?.permalinks}
                  className="group block mb-6 sm:mb-5 xl:mb-9 last:mb-0 overflow-hidden"
                >
                  <div className="flex gap-3">
                    <div className="w-28 md:w-29.25 h-22.75 sm:h-20 xl:h-22.75 rounded-xl shrink-0 relative overflow-hidden">
                      <Image
                        src={getBlogImageSrc(blog) || "/images/default-thumbnail.jpg"}
                        alt="Thumbnail"
                        fill
                        className="rounded-xl object-cover object-left transition group-hover:scale-[1.03] z-3"
                      />
                    </div>

                    <div className="flex flex-col justify-center gap-2 md:gap-0 md:justify-between">
                      <p className="text-2sm xl:text-base xl:leading-6 leading-1.2 font-semibold text-black text-left line-clamp-3 transition group-hover:text-primaryBlue">
                        {blog?.txttitle}
                      </p>

                      <ul className="text-[#6E6E73] flex gap-2 font-medium sm:font-bold text-xs sm:text-sm">
                        <li className="flex items-center gap-1">
                          <Icon.viewEyeIcon />
                          {formatViews(blog?.view ?? 0)}
                        </li>
                        <li className="flex items-center gap-2 before:content-['•'] before:inline-block before:text-[#6E6E73]">
                          {getRelativeTime(formatDate(blog?.publish_date ?? new Date()))}
                        </li>
                      </ul>
                    </div>
                  </div>
                </a>
              ))
            : videos.slice(1, 4).map((video, ind) => <RelatedSmallVideoCards key={video.id ?? ind} video={video} />)}
        </div>
      </div>

      <a
        href={activeTab === "blogs" ? BLOG_ROOT : `${ROOT_URL}mutual-fund-videos`}
        aria-label={"See All" + activeTab === "blogs" ? "Blogs" : "Videos"}
        className="mx-auto max-w-80 flex items-center justify-center gap-3 sm:text-lg font-semibold text-(--secondary) bg-white border border-(--secondary) rounded-xl py-2.5 sm:py-3 transition hover:bg-(--secondary) hover:text-white"
      >
        See All {activeTab === "blogs" ? "Blogs" : "Videos"}
        <Icon.rightArrow  />
      </a>
    </>
  );
}
